import { PIN_MAX_DIGITS, PIN_MIN_DIGITS } from '@worklogix/shared';

/**
 * The PIN-pad's DECISIONS, factored out of the component so they can be tested
 * directly (K6).
 *
 * This follows the kiosk's existing convention — see `photo/captureReason.ts` and
 * `photo/photoDrain.ts`, where the same thing was done for the capture and drain
 * rules. The kiosk deliberately has **no jsdom and no testing-library**
 * (`vitest.config.ts` says so explicitly), so a rule that lives inside a component
 * is a rule with no test. Adding a DOM test stack days before go-live to cover four
 * lines would be the wrong trade; moving the four lines out is the right one.
 *
 * Worth covering because this is the single most-touched UI in the product: every
 * employee uses it at every shift change from Sep 9, and the digit floor moved from
 * 4 to 6 (audit #7) without any test.
 *
 * Both bounds come from the SHARED policy so the pad and the API cannot drift apart.
 */

/** Can another digit be added, or is the PIN already at its maximum length? */
export function canAppendDigit(pin: string): boolean {
  return pin.length < PIN_MAX_DIGITS;
}

/**
 * The PIN after pressing `digit` — unchanged once at maximum length.
 *
 * Returns the new value rather than mutating, so the caller's state setter stays a
 * pure update and a key held down cannot grow the PIN without bound.
 */
export function appendDigit(pin: string, digit: string): string {
  return canAppendDigit(pin) ? pin + digit : pin;
}

/**
 * May this PIN be submitted?
 *
 * Enforces the FLOOR, not merely "something was typed". A too-short PIN would
 * otherwise be sent, fail to resolve server-side, and come back as the very same
 * "PIN not recognized" an employee gets for a genuinely wrong PIN — while also
 * burning a wrong-PIN attempt against the kiosk lockout on the way. Both of those
 * are worse than the button simply staying inert.
 */
export function canSubmitPin(pin: string, disabled: boolean): boolean {
  return !disabled && pin.length >= PIN_MIN_DIGITS;
}
